import axios, { type AxiosError, type AxiosResponse } from 'axios'
import { API_BASE_URL, API_TIMEOUT, STORAGE_KEYS, ROUTES } from '@/constants'
import { getRouter } from '@/router/instance'
import type {
	ApiResponse,
	UploadImageResponse,
	AnalyzeImagesResponse,
	SelectSkillsResponse,
	GenerateResponse,
	TaotuQuoteResponse,
	RegenerateImageResponse,
	AddTextToImageResponse,
	LoginResponse,
	TaskResultData,
	User,
} from '@/types'

const api = axios.create({
	baseURL: API_BASE_URL,
	timeout: API_TIMEOUT,
})

// 请求拦截器
api.interceptors.request.use((config) => {
	const token = localStorage.getItem(STORAGE_KEYS.TOKEN)
	if (token) {
		config.headers.Authorization = `Bearer ${token}`
	}
	return config
})

// 响应拦截器
api.interceptors.response.use(
	(response: AxiosResponse) => response,
	(error: AxiosError) => {
		const status = error.response?.status
		if (status === 401 || status === 422) {
			localStorage.removeItem(STORAGE_KEYS.TOKEN)
			const router = getRouter()
			if (router) {
				const currentPath = router.currentRoute.value.path
				if (!currentPath.startsWith(ROUTES.AUTH)) {
					router.push(ROUTES.AUTH)
				}
			}
		} else if (status === 403) {
			const router = getRouter()
			if (router) {
				const currentPath = router.currentRoute.value.path
				if (!currentPath.startsWith(ROUTES.ERROR_403)) {
					router.push(ROUTES.ERROR_403)
				}
			}
		}
		return Promise.reject(error)
	},
)

// 类型辅助函数
function isAxiosError(error: unknown): error is AxiosError {
	return (error as AxiosError).isAxiosError === true
}

function getErrorMessage(error: unknown): string {
	if (isAxiosError(error)) {
		const data = error.response?.data as { detail?: string }
		return data?.detail || error.message || '请求失败'
	}
	if (error instanceof Error) {
		return error.message
	}
	return '发生未知错误'
}

// Image APIs
export async function uploadImage(file: File) {
	const formData = new FormData()
	formData.append('file', file)
	const res = await api.post('/images/upload', formData)
	return res.data as unknown as UploadImageResponse
}

export async function batchUploadImages(files: File[]) {
	const formData = new FormData()
	for (const file of files) {
		formData.append('files', file)
	}
	return api.post<ApiResponse<{ results: UploadImageResponse[] }>>('/images/batch-upload', formData)
}

export async function analyzeImages(
	object_names: string[],
	user_prompt?: string,
	materialFiles?: File[],
	materialText?: string,
	taotuLanguage?: { code: string; name: string; direction: string },
	original_images?: Array<{ index: number; object_name: string; size?: number | null }>,
) {

	const formData = new FormData()
	formData.append('object_names', JSON.stringify(object_names))
	if (original_images?.length) {
		formData.append('original_images', JSON.stringify(original_images))
	}
	if (user_prompt) {
		formData.append('user_prompt', user_prompt)
	}
	if (materialText) {
		formData.append('material_text', materialText)
	}
	if (materialFiles?.length) {
		for (const file of materialFiles) {
			formData.append('material_files', file)
		}
	}
	if (taotuLanguage) {
		formData.append('taotu_language', JSON.stringify(taotuLanguage))
	}

	if (import.meta.env.DEV) {
		console.group('%c🔍 [MaterialDebug] FormData 内容', 'color: #2196f3; font-weight: bold')
		for (const [key, value] of formData.entries()) {
			if (typeof value === 'string') {
				console.log(`  ${key}:`, value.length > 200 ? value.substring(0, 200) + '...' : value)
			} else {
				console.log(`  ${key}: [File] ${value.name} (${value.size} bytes, ${value.type})`)
			}
		}
		console.groupEnd()
	}

	return api.post<ApiResponse<{ task_id: string; status: string; object_names: string[] }>>(
		'/images/analyze',
		formData,
	)
}

export async function replaceAndReanalyze(
	taskId: string,
	replace_index: number,
	new_object_name: string,
	new_size?: number,
) {
	return api.post<ApiResponse>(`/tasks/${taskId}/replace-and-reanalyze`, {
		replace_index,
		new_object_name,
		new_size,
	})
}

// Combo APIs
export async function selectSkills(
	analysis_results: Record<string, unknown>[],
	selected_indices: number[],
) {
	return api.post<ApiResponse<SelectSkillsResponse>>('/combo/select-skills', {
		analysis_results,
		selected_indices,
	})
}

export async function generate(params: {
	task_id?: string
	selected_skills: Array<{ skill_key: string; template_name: string }>
	selected_indices: number[]
	primary_image_index: number
	_analysis_results?: Record<string, unknown>[]
	user_prompt?: string
	taotu_quote_id?: string
	sku_quote_id?: string
	user_edited_material?: Record<string, unknown>
}) {
	return api.post<ApiResponse<GenerateResponse>>('/combo/generate', params)
}

export async function quoteTaotu(params: {
	task_id: string
	selected_indices: number[]
	_analysis_results?: Record<string, unknown>[]
	user_prompt?: string
	taotu_language?: { code: string; name: string; direction: string }
}) {
	return api.post<TaotuQuoteResponse>('/combo/taotu/quote', params)
}

export async function requestTaotuQuote(taskId: string) {
	const res = await api.post(`/combo/${taskId}/taotu-quote`)
	return res.data as TaotuQuoteResponse
}

export async function updateMaterial(taskId: string, material: Record<string, unknown>) {
	const res = await api.put(`/images/${taskId}/material`, { material })
	return res.data as { success: boolean }
}

// Smart selection APIs
export async function analyzeCombo(taskId: string) {
	return api.post<ApiResponse>(`/combo/analyze-combo/${taskId}`)
}

// Task APIs
export async function getTask(taskId: string) {
	return api.get<ApiResponse>(`/tasks/${taskId}`)
}

export async function getTaskResult(taskId: string) {
	const res = await api.get(`/tasks/${taskId}/result`)
	return res.data as unknown as TaskResultData
}

export async function getTasks(page: number = 1, pageSize: number = 20) {
	return api.get<ApiResponse>('/tasks', { params: { page, page_size: pageSize } })
}

export async function cancelTask(taskId: string) {
	return api.post<ApiResponse>(`/tasks/${taskId}/cancel`)
}

export async function retryTask(taskId: string, new_prompt?: string) {
	return api.post<ApiResponse>(`/tasks/${taskId}/retry`, { new_prompt })
}

export async function linkComboImage(
	taskId: string,
	params: { object_name: string; skill_key: string; template_name: string },
) {
	return api.post<ApiResponse>(`/tasks/${taskId}/link-combo-image`, params)
}

// Taotu Display Style APIs
export async function generateTaotuDisplayStyle(taskId: string, expectedVersion?: number) {
	const res = await api.post(`/tasks/${taskId}/taotu-display-style/generate`, {
		expected_version: expectedVersion,
	})
	return res.data as { success: boolean; display_style: import('@/types').TaotuDisplayStyle }
}

export async function resetTaotuDisplayStyle(taskId: string, expectedVersion?: number) {
	const res = await api.post(`/tasks/${taskId}/taotu-display-style/reset`, {
		expected_version: expectedVersion,
	})
	return res.data as { success: boolean; display_style: import('@/types').TaotuDisplayStyle }
}

// Taotu Regenerate API
export async function regenerateTaotuImage(
	taskId: string,
	objectName: string,
	suggestion?: string,
) {
	const res = await api.post(`/tasks/${taskId}/taotu-regenerate`, {
		object_name: objectName,
		suggestion: suggestion || undefined,
	})
	return res.data as {
		success: boolean
		regeneration_task_id: string
		credits_charged?: number
		balance_after?: number
	}
}

// Taotu Regenerate Retry API (超时重试)
export async function retryTaotuRegenerate(taskId: string, regenerationTaskId: string) {
	const res = await api.post(`/tasks/${taskId}/taotu-regenerate-retry`, {
		regeneration_task_id: regenerationTaskId,
	})
	return res.data as { success: boolean }
}

// CropRect type for local inpaint
export interface CropRect {
	x: number
	y: number
	width: number
	height: number
}

// Taotu Inpaint API (局部重绘)
export async function inpaintTaotuImage(
	taskId: string,
	objectName: string,
	cropRect: CropRect,
	prompt: string,
	referenceImages: File[] = [],
) {
	const formData = new FormData()
	formData.append('object_name', objectName)
	formData.append('crop_rect', JSON.stringify(cropRect))
	formData.append('prompt', prompt)
	referenceImages.forEach((file) => {
		formData.append('reference_images', file, file.name)
	})
	const res = await api.post(`/tasks/${taskId}/taotu-inpaint`, formData, {
		headers: { 'Content-Type': 'multipart/form-data' },
	})
	return res.data as {
		success: boolean
		regeneration_task_id: string
		credits_charged?: number
		balance_after?: number
	}
}

// SKU Regenerate API
export async function regenerateSkuImage(
	taskId: string,
	objectName: string,
	suggestion?: string,
) {
	const res = await api.post(`/tasks/${taskId}/sku-regenerate`, {
		object_name: objectName,
		suggestion: suggestion || undefined,
	})
	return res.data as {
		success: boolean
		regeneration_task_id: string
		credits_charged?: number
		balance_after?: number
	}
}

// SKU Inpaint API (局部重绘)
export async function inpaintSkuImage(
	taskId: string,
	objectName: string,
	cropRect: CropRect,
	prompt: string,
	referenceImages: File[] = [],
) {
	const formData = new FormData()
	formData.append('object_name', objectName)
	formData.append('crop_rect', JSON.stringify(cropRect))
	formData.append('prompt', prompt)
	referenceImages.forEach((file) => {
		formData.append('reference_images', file, file.name)
	})
	const res = await api.post(`/tasks/${taskId}/sku-inpaint`, formData, {
		headers: { 'Content-Type': 'multipart/form-data' },
	})
	return res.data as {
		success: boolean
		regeneration_task_id: string
		credits_charged?: number
		balance_after?: number
	}
}

// Poster Image Inpaint API (AI营销海报局部重绘)
export async function inpaintPosterImage(
	taskId: string,
	objectName: string,
	cropRect: CropRect,
	prompt: string,
	referenceImages: File[] = [],
) {
	const formData = new FormData()
	formData.append('task_id', taskId)
	formData.append('object_name', objectName)
	formData.append('crop_rect', JSON.stringify(cropRect))
	formData.append('prompt', prompt)
	referenceImages.forEach((file) => {
		formData.append('reference_images', file, file.name)
	})
	const res = await api.post('/images/inpaint-poster', formData, {
		headers: { 'Content-Type': 'multipart/form-data' },
	})
	return res.data as {
		success: boolean
		regeneration_task_id: string
		credits_charged?: number
		balance_after?: number
	}
}

// White BG Image Inpaint API (白底图局部重绘)
export async function inpaintWhiteBgImage(
	taskId: string,
	objectName: string,
	cropRect: CropRect,
	prompt: string,
	referenceImages: File[] = [],
) {
	const formData = new FormData()
	formData.append('task_id', taskId)
	formData.append('object_name', objectName)
	formData.append('crop_rect', JSON.stringify(cropRect))
	formData.append('prompt', prompt)
	referenceImages.forEach((file) => {
		formData.append('reference_images', file, file.name)
	})
	const res = await api.post('/images/inpaint', formData, {
		headers: { 'Content-Type': 'multipart/form-data' },
	})
	return res.data as {
		success: boolean
		regeneration_task_id: string
		credits_charged?: number
		balance_after?: number
	}
}

// Template API
export async function getTemplateData(
	taskId: string,
	templateName: string,
	skillKey?: string,
) {
	return api.get<ApiResponse>('/template', {
		params: { task_id: taskId, template_name: templateName, skill_key: skillKey },
	})
}

export async function updateTemplateData(params: {
	task_id: string
	template_name: string
	skill_key?: string
	layout: Record<string, unknown>[]
}) {
	return api.put<ApiResponse>('/template', params)
}

// Image operation APIs
export async function regenerateImage(objectName: string, params: Record<string, unknown>) {
	return api.post<
		ApiResponse<
			RegenerateImageResponse & {
				credits_charged?: number
				balance_after?: number
			}
		>
	>('/images/regenerate', {
		source_object_name: objectName,
		...params,
	})
}

export async function regenerateModelImage(taskId: string, userPrompt?: string) {
	return api.post<ApiResponse<{ status: string; task_id: string }>>(
		`/tasks/${taskId}/model-regenerate`,
		{ user_prompt: userPrompt || '' },
	)
}

export async function addTextToImage(objectName: string, params: Record<string, unknown>) {
	return api.post<ApiResponse<AddTextToImageResponse>>('/images/add-text', {
		source_object_name: objectName,
		...params,
	})
}

export async function replaceImage(
	taskId: string,
	params: {
		template_name: string
		old_object_name: string
		new_object_name: string
	},
) {
	return api.post<ApiResponse>(`/tasks/${taskId}/replace-image`, params)
}

// Auth APIs
export async function sendCode(phone: string) {
	return api.post<ApiResponse>('/auth/send_code', { phone })
}

export async function login(phone: string, code: string) {
	const res = await api.post('/auth/login', { phone, code })
	return res.data as unknown as LoginResponse
}

export async function getUserInfo() {
	const res = await api.get('/user/info')
	return res.data as unknown as User
}

export async function getMyCreditLogs(params: {
	page?: number
	page_size?: number
}) {
	return api.get<ApiResponse>('/user/credit-logs', { params })
}

export async function getAdminUsers(phone_search?: string, page = 1, page_size = 20) {
	return api.get<ApiResponse>('/admin/users', { params: { phone_search, page, page_size } })
}

export async function addAdminCredits(userId: string, amount: number) {
	return api.post<ApiResponse>(`/admin/users/${userId}/add_credits`, {}, { params: { amount } })
}

export async function deductAdminCredits(userId: string, amount: number) {
	return api.post<ApiResponse>(
		`/admin/users/${userId}/deduct_credits`,
		{},
		{ params: { amount, reason: '管理员手动扣减' } },
	)
}

export async function getAdminCreditLogs(params: {
	user_id?: string
	sort_order?: 'asc' | 'desc'
	page?: number
	page_size?: number
}) {
	return api.get<ApiResponse>('/admin/credit-logs', { params })
}

export async function getAdminTasks(params: {
	user_id?: string
	phone?: string
	task_id?: string
	status?: string
	page?: number
	page_size?: number
}) {
	return api.get<ApiResponse>('/admin/tasks', { params })
}

export async function resetAdminTask(taskId: string) {
	return api.post<ApiResponse>(`/admin/tasks/${taskId}/reset`)
}

// Payment APIs
export async function getProducts() {
	const res = await api.get('/payment/products')
	return res.data as Array<{ id: string; name: string; credits: number; price_yuan: number }>
}

export async function createPaymentOrder(productId: string, payMethod?: string, quantity = 1) {
	const res = await api.post('/payment/create-order', {
		product_id: productId,
		pay_method: payMethod || undefined,
		quantity,
	})
	return res.data as {
		order_no: string
		client_order_no: string
		qr_url: string
		qr_data_url: string
		amount: number
		amount_yuan: number
		credits: number
		quantity: number
		status: string
	}
}

export async function queryPaymentOrder(orderNo: string) {
	const res = await api.get(`/payment/order/${orderNo}`)
	return res.data as {
		order_no: string
		status: string
		qr_url: string
		qr_data_url: string
		amount: number
		amount_yuan: number
		credits: number
		paid_at: string | null
	}
}

export async function getPaymentOrders() {
	const res = await api.get('/payment/orders')
	return res.data as Array<{
		order_no: string
		status: string
		amount: number
		amount_yuan: number
		credits: number
		created_at: string | null
		paid_at: string | null
	}>
}

// Admin Template APIs (read-only)
export async function getAdminTemplates(params?: { category?: string; status?: string }) {
	return api.get<ApiResponse>('/admin/templates', { params })
}

export async function getAdminTemplateDetail(templateId: string) {
	return api.get<ApiResponse>(`/admin/templates/${templateId}`)
}

export async function reloadAdminTemplates() {
	return api.post<ApiResponse>('/admin/templates/reload')
}

export async function getAdminTaskTrace(taskId: string) {
	return api.get<ApiResponse>(`/admin/tasks/${taskId}/trace`)
}

export function adminGenerateTaotuImage(
	taskId: string,
	params: {
		prompt: string
		source_object_names: string[]
		photo_type_key: string
		sequence: number
	},
) {
	return api.post<ApiResponse>(`/admin/tasks/${taskId}/generate-taotu-image`, params)
}

export function adminGenerateSkuImage(
	taskId: string,
	params: {
		prompt: string
		source_object_names: string[]
		style_reference_object_name?: string
		sequence: number
	},
) {
	return api.post<ApiResponse>(`/admin/tasks/${taskId}/generate-sku-image`, params)
}

export async function learnSkillsAdmin(images: File[], hint = '') {
	const form = new FormData()
	images.forEach((f) => form.append('images', f))
	if (hint) form.append('hint', hint)
	const res = await api.post('/admin/skills/learn', form, {
		timeout: 300_000, // 5 minutes
		headers: { 'Content-Type': 'multipart/form-data' },
	})
	return res.data as {
		skills: Array<{
			skill_id: string
			skill_name: string
			category_path: string
			photo_type: string
			product_category: string
			brand: string
			file_path: string
			status: string
			strategy: string
			similarity_score: number
			matched_skill_id: string
			reason: string
			taotu_role: string
			images_count: number
			error: string
		}>
		total_skills: number
		total_images: number
	}
}

// ── Skill Learning (async task flow) ──────────────────────────────────────

export interface SkillLearningDirective {
	version?: number
	scope?: string
	declared_photo_type?: string
	declared_category_path?: string
	learning_focus?: string[]
	brand_hint?: string
	ignored_brand_hint?: string
	product_category_hint?: string
	instruction_strength?: string
	raw_hint?: string
	parse_status?: string
	confidence?: number
}

export interface SkillLearningTask {
	task_id: string
	status: string
	progress: {
		stage: string
		current: number
		total: number
		percent: number
		message: string
	} | null
	hint: string
	learning_directive?: SkillLearningDirective
	directive_parse_status?: string
	image_count: number
	input_images: Array<{
		index: number
		object_name: string
		filename: string
		content_type: string
		size: number
	}>
	result_ids: string[]
	results: Array<{
		result_id: string
		skill_id: string
		skill_name: string
		category_path: string
		photo_type: string
		strategy: string
		status: string
		similarity_score: number
		file_path: string
		error: string
	}>
	error_message: string | null
	created_at: string | null
	updated_at: string | null
	completed_at: string | null
}

export interface SkillLearningResult {
	result_id: string
	task_id: string
	hint: string
	learning_directive?: SkillLearningDirective
	directive_parse_status?: string
	input_images: Array<{ index: number; object_name: string }>
	skill_id: string
	skill_name: string
	category_path: string
	photo_type: string
	product_category: string
	brand: string
	strategy: string
	status: string
	similarity_score: number
	matched_skill_id: string
	taotu_role: string
	file_path: string
	skill_markdown_excerpt: string
	reason: string
	error: string
	strategy_trace: Record<string, any>
	similarity_breakdown: Record<string, any>
	skill_identity_key: string
	created_at: string | null
}

export interface SkillLearningResultsResponse {
	items: SkillLearningResult[]
	total: number
	page: number
	page_size: number
	total_pages: number
}

export interface SkillLearningTaskListItem {
	task_id: string
	status: string
	progress: {
		stage: string
		current: number
		total: number
		percent: number
		message: string
	} | null
	hint: string
	learning_directive?: SkillLearningDirective
	directive_parse_status?: string
	image_count: number
	input_images: Array<{
		index: number
		object_name: string
		filename: string
		content_type: string
		size: number
	}>
	result_ids: string[]
	error_message: string | null
	retry_count: number
	retryable: boolean
	created_at: string | null
	updated_at: string | null
	completed_at: string | null
}

export interface SkillLearningTasksResponse {
	items: SkillLearningTaskListItem[]
	total: number
	page: number
	page_size: number
	total_pages: number
}

export async function createSkillLearningTask(
	images: File[],
	hint = '',
	options?: {
		photo_type?: string
		category_path?: string
		learning_focus?: string[]
	},
) {
	const form = new FormData()
	images.forEach((f) => form.append('images', f))
	if (hint) form.append('hint', hint)
	if (options?.photo_type) form.append('photo_type', options.photo_type)
	if (options?.category_path) form.append('category_path', options.category_path)
	if (options?.learning_focus?.length) {
		form.append('learning_focus', JSON.stringify(options.learning_focus))
	}
	const res = await api.post('/admin/skill-learning/tasks', form, {
		headers: { 'Content-Type': 'multipart/form-data' },
	})
	return res.data as { task_id: string; status: string; created_at: string }
}

export async function getSkillLearningTasks(params?: {
	page?: number
	page_size?: number
	status?: string
}) {
	const res = await api.get('/admin/skill-learning/tasks', { params })
	return res.data as SkillLearningTasksResponse
}

export async function getSkillLearningTask(taskId: string) {
	const res = await api.get(`/admin/skill-learning/tasks/${taskId}`)
	return res.data as SkillLearningTask
}

export async function getSkillLearningTrace(taskId: string) {
	const res = await api.get(`/admin/skill-learning/tasks/${taskId}/trace`)
	return res.data as {
		task_id: string
		trace_version: number
		events: Array<{
			stage: string
			event: string
			status: string
			created_at: string
			[key: string]: unknown
		}>
		stage_inputs: Record<string, unknown>
		stage_outputs: Record<string, unknown>
		llm_calls: unknown[]
		similarity_matches: unknown[]
		strategy_decisions: unknown[]
		groups: unknown[]
		file_writes: unknown[]
		errors: unknown[]
	}
}

export async function getSkillLearningResults(params?: {
	page?: number
	page_size?: number
	photo_type?: string
	category_path?: string
	keyword?: string
}) {
	const res = await api.get('/admin/skill-learning/results', { params })
	return res.data as SkillLearningResultsResponse
}

export async function getSkillLearningResultDetail(resultId: string) {
	const res = await api.get(`/admin/skill-learning/results/${resultId}`)
	return res.data as SkillLearningResult
}

export async function deleteSkillLearningResult(resultId: string) {
	const res = await api.delete(`/admin/skill-learning/results/${resultId}`)
	return res.data as { ok: boolean }
}

export async function retrySkillLearningTask(taskId: string) {
	const res = await api.post(`/admin/skill-learning/tasks/${taskId}/retry`)
	return res.data as { task_id: string; status: string; retry_count: number }
}

// ── Skill Management APIs ─────────────────────────────────────────

export interface SkillListItem {
  _id: string
  skill_key: string
  type: string
  name: string
  description: string
  source_path: string
  version: number
  updated_at: string
}

export interface SkillDetail extends SkillListItem {
  content: string
  metadata: Record<string, unknown>
  migrated_from_file: boolean
  created_at: string
  enabled_version_id?: string | null
  versions_count?: number
}

export interface SkillTree {
  name: string
  skill_key: string
  has_parent: boolean
  children: { name: string; skill_key: string; description: string }[]
}

export async function listSkills(params: {
  type?: string
  keyword?: string
  page?: number
  page_size?: number
}) {
  const res = await api.get('/admin/skills-mgmt', { params })
  return res.data as { items: SkillListItem[]; total: number; page: number; page_size: number }
}

export async function getSkillTree() {
  const res = await api.get('/admin/skills-mgmt/tree')
  return res.data as { tree: SkillTree[] }
}

export async function getSkill(skillKey: string) {
  const res = await api.get(`/admin/skills-mgmt/${encodeURIComponent(skillKey)}`)
  return res.data as SkillDetail
}

export async function updateSkill(skillKey: string, content: string, version: number) {
  const res = await api.put(
    `/admin/skills-mgmt/${encodeURIComponent(skillKey)}`,
    { content, version },
  )
  return res.data as { success: boolean }
}

export async function getSkillStats() {
  const res = await api.get('/admin/skills-mgmt/stats')
  return res.data as Record<string, number>
}

export async function importSkills(force = false) {
  const res = await api.post('/admin/skills-mgmt/import', { force })
  return res.data as { imported: number; errors: string[] }
}

// ── Skill Version APIs ──────────────────────────────────────────────

export interface SkillVersionItem {
  _id: string
  skill_key: string
  version_id: string
  status: 'enabled' | 'disabled'
  reason: string
  created_by: string
  created_at: string
}

export interface SkillVersionDetail extends SkillVersionItem {
  content: string
  proposal_id: string | null
}

export interface SkillVersionsResponse {
  versions: SkillVersionItem[]
  total: number
  page: number
  page_size: number
  enabled_version_id: string | null
}

export async function listSkillVersions(skillKey: string, page = 1, pageSize = 20) {
  const encodedKey = encodeURIComponent(skillKey)
  const res = await api.get(`/admin/skills-mgmt/versions/${encodedKey}`, {
    params: { page, page_size: pageSize },
  })
  return res.data as SkillVersionsResponse
}

export async function getSkillVersion(skillKey: string, versionId: string) {
  const encodedKey = encodeURIComponent(skillKey)
  const res = await api.get(`/admin/skills-mgmt/versions/${encodedKey}/${versionId}`)
  return res.data as SkillVersionDetail
}

export async function enableSkillVersion(skillKey: string, versionId: string) {
  const encodedKey = encodeURIComponent(skillKey)
  const res = await api.put(`/admin/skills-mgmt/versions/${encodedKey}/${versionId}/enable`)
  return res.data as { success: boolean }
}

export async function deleteSkillVersion(skillKey: string, versionId: string) {
  const encodedKey = encodeURIComponent(skillKey)
  const res = await api.delete(`/admin/skills-mgmt/versions/${encodedKey}/${versionId}`)
  return res.data as { success: boolean }
}

// ── Pending Proposals APIs ─────────────────────────────────────────

export interface PendingProposalItem {
  proposal_id: string
  skills_count: number
  skills: string[]
  reasons: string[]
  created_at: string
}

export interface PendingProposalsResponse {
  proposals: PendingProposalItem[]
}

export interface PendingProposalDetail {
  proposal_id: string
  changes: { skill_key: string; new_content: string; reason: string }[]
  created_at: string
}

export async function listPendingProposals() {
  const res = await api.get('/admin/skills-mgmt/pending-proposals')
  return res.data as PendingProposalsResponse
}

export async function getPendingProposal(proposalId: string) {
  const res = await api.get(`/admin/skills-mgmt/pending-proposals/${proposalId}`)
  return res.data as PendingProposalDetail
}

// ── Change Snapshots APIs ─────────────────────────────────────────

export interface ChangeSnapshotItem {
  _id: string
  label: string
  source: string
  source_id: string
  created_at: string
  skills_count: number
}

export interface ChangeSnapshotsResponse {
  items: ChangeSnapshotItem[]
  total: number
  page: number
  page_size: number
}

export interface ChangeSnapshotDetail {
  _id: string
  label: string
  source: string
  source_id: string
  created_at: string
  snapshot: Record<string, { version_id: string }>
  skills_count: number
}

export interface RollbackResult {
  success: boolean
  results: {
    rolled_back: number
    deleted: number
    errors: string[]
  }
}

export async function listChangeSnapshots(page = 1, pageSize = 50) {
  const res = await api.get('/admin/skills-mgmt/change-snapshots', {
    params: { page, page_size: pageSize },
  })
  return res.data as ChangeSnapshotsResponse
}

export async function getChangeSnapshot(changeId: string) {
  const res = await api.get(`/admin/skills-mgmt/change-snapshots/${changeId}`)
  return res.data as ChangeSnapshotDetail
}

export async function rollbackToSnapshot(changeId: string) {
  const res = await api.post(`/admin/skills-mgmt/change-snapshots/${changeId}/rollback`)
  return res.data as RollbackResult
}

// ── Snapshot Diff APIs ───────────────────────────────────────────

export interface DiffLine {
  type: 'header' | 'insert' | 'delete' | 'equal'
  text: string
}

export interface SkillDiff {
  skill_key: string
  before_version_id: string
  after_version_id: string
  before_size: number
  after_size: number
  lines: DiffLine[]
  truncated: boolean
  stats: { additions: number; deletions: number; context: number }
}

export interface SnapshotDiffResult {
  change_id: string
  diffs: SkillDiff[]
  note?: string
}

export async function getSnapshotDiff(changeId: string) {
  const res = await api.get(`/admin/skills-mgmt/change-snapshots/${changeId}/diff`)
  return res.data as SnapshotDiffResult
}

// ── Poster Design Styles Learning ────────────────────────────────────────

export interface PosterStyleAnalysisResult {
  index: number
  filename: string
  status: 'success' | 'error'
  style_id?: string
  name?: string
  mood?: string
  layout_pattern?: string
  color_palette?: string[]
  oss_url?: string
  error?: string
}

export interface PosterStyleBatchResult {
  total: number
  success: number
  failed: number
  results: PosterStyleAnalysisResult[]
}

export interface PosterStyle {
  _id: string
  style_id: string
  name: string
  source_image: { object_name: string; oss_url: string; filename: string }
  color_palette: string[]
  background_description: string
  typography_style: string
  decorative_elements: string
  layout_pattern: string
  mood: string
  prompt_injection: string
  created_at: string
}

export async function batchAnalyzePosterStyles(files: File[]): Promise<PosterStyleBatchResult> {
  const formData = new FormData()
  for (const file of files) {
    formData.append('files', file)
  }
  const res = await api.post('/admin/poster-styles/batch-analyze', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000, // 5min for batch VLM analysis
  })
  return res.data as PosterStyleBatchResult
}

export async function listPosterStyles(page = 1, pageSize = 50) {
  const res = await api.get('/admin/poster-styles', { params: { page, page_size: pageSize } })
  return res.data as { total: number; items: PosterStyle[] }
}

export async function deletePosterStyle(styleId: string) {
  const res = await api.delete(`/admin/poster-styles/${styleId}`)
  return res.data
}

// ── Skill Usage Statistics ──────────────────────────────────────

export interface SkillStatItem {
  skill_key: string
  skill_type: string
  skill_name: string
  count: number
}

export interface SkillStatsResponse {
  items: SkillStatItem[]
  total: number
  page: number
  page_size: number
}

export interface SkillUsageRecord {
  used_at: string
  source_images: string[]
  output_images: string[]
  user_id: string
  task_id: string
}

export interface SkillDetailResponse {
  skill_key: string
  total: number
  page: number
  page_size: number
  records: SkillUsageRecord[]
}

export async function listSkillStats(
  type?: string,
  keyword?: string,
  page = 1,
  pageSize = 50,
): Promise<SkillStatsResponse> {
  const res = await api.get('/admin/skill-stats', {
    params: { type, keyword, page, page_size: pageSize },
  })
  return res.data as SkillStatsResponse
}

export async function getSkillDetail(
  skillKey: string,
  page = 1,
  pageSize = 100,
): Promise<SkillDetailResponse> {
  const res = await api.get('/admin/skill-stats/detail', {
    params: { skill_key: skillKey, page, page_size: pageSize },
  })
  return res.data as SkillDetailResponse
}

// ── Model API Statistics ──────────────────────────────────────────

export interface ModelLogItem {
  _id: string
  api_url: string
  model_id: string
  request_id: string
  call_type: string
  request_params: Record<string, any>
  request_image_sizes: { width: number; height: number; tokens: number }[]
  request_image_urls: string[]
  response_image_sizes: { width: number; height: number; tokens: number }[]
  response_image_urls: string[]
  text_tokens: number
  image_tokens: number
  total_tokens: number
  response_time_ms: number
  success: boolean
  error_message: string
  task_id: string
  user_id: string
  extra: Record<string, any>
  created_at: string
}

export interface ModelLogsResponse {
  total: number
  page: number
  page_size: number
  total_pages: number
  logs: ModelLogItem[]
}

export interface ModelStatsSummary {
  by_model: {
    model_id: string
    call_type: string
    count: number
    success_count: number
    fail_count: number
    success_rate: number
    total_text_tokens: number
    total_image_tokens: number
    total_tokens: number
    avg_response_time_ms: number
    max_response_time_ms: number
    min_response_time_ms: number
  }[]
  overall: {
    total_calls: number
    total_text_tokens: number
    total_image_tokens: number
    total_tokens: number
    avg_response_time_ms: number
  }
}

export async function listModelLogs(
  page = 1,
  pageSize = 50,
  modelId?: string,
  callType?: string,
  success?: string,
  startTime?: string,
  endTime?: string,
  taskId?: string,
): Promise<ModelLogsResponse> {
  const res = await api.get('/admin/model-stats', {
    params: {
      page,
      page_size: pageSize,
      model_id: modelId,
      call_type: callType,
      success,
      start_time: startTime,
      end_time: endTime,
      task_id: taskId,
    },
  })
  return res.data as ModelLogsResponse
}

export async function getModelStatsSummary(
  startTime?: string,
  endTime?: string,
): Promise<ModelStatsSummary> {
  const res = await api.get('/admin/model-stats/summary', {
    params: { start_time: startTime, end_time: endTime },
  })
  return res.data as ModelStatsSummary
}

export async function getModelLogDetail(logId: string): Promise<ModelLogItem> {
  const res = await api.get('/admin/model-stats/detail', {
    params: { log_id: logId },
  })
  return res.data as ModelLogItem
}

export { api, getErrorMessage, isAxiosError }
